import type { AudioManager } from '../../audio/AudioManager';
import type { TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawBlade } from '../../render/WeaponRenderer';
import { drawButton, pointInRect } from '../../render/ui/UI';
import type { BattleOutcome, Hero, Rect } from '../../types';
import type { Phase } from '../Phase';

const ADVANCE_BUTTON: Rect = { x: 60, y: 560, w: LOGICAL_WIDTH - 120, h: 56 };

export class ResultPhase implements Phase {
  private outcome: BattleOutcome;
  private hero: Hero;
  private audio: AudioManager;
  private done = false;
  private unbind: (() => void) | null = null;
  private elapsed = 0;

  constructor(outcome: BattleOutcome, hero: Hero, input: TouchInput, audio: AudioManager) {
    this.outcome = outcome;
    this.hero = hero;
    this.audio = audio;
    this.unbind = input.onPointerDown((e) => {
      if (this.elapsed > 0.8 && pointInRect(e.pos.x, e.pos.y, ADVANCE_BUTTON)) {
        this.audio.play('bellows_tap');
        this.done = true;
      }
    });
  }

  update(dt: number): void {
    this.elapsed += dt;
  }

  isComplete(): boolean {
    return this.done;
  }

  dispose(): void {
    this.unbind?.();
    this.unbind = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const grad = ctx.createLinearGradient(0, 0, 0, LOGICAL_HEIGHT);
    if (this.outcome.result === 'victory') {
      grad.addColorStop(0, '#1a1a10');
      grad.addColorStop(1, '#0a0a06');
    } else if (this.outcome.result === 'hero_death') {
      grad.addColorStop(0, '#1a0606');
      grad.addColorStop(1, '#0a0303');
    } else {
      grad.addColorStop(0, '#0a0a10');
      grad.addColorStop(1, '#050507');
    }
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    // Title.
    const titleColor =
      this.outcome.result === 'victory'
        ? '#ffd84a'
        : this.outcome.result === 'hero_death'
          ? '#ff6a4a'
          : '#a8a0c4';
    const title =
      this.outcome.result === 'victory'
        ? 'VICTORY'
        : this.outcome.result === 'hero_death'
          ? `${this.hero.name.toUpperCase()} HAS FALLEN`
          : 'BLADE BROKEN';
    ctx.fillStyle = titleColor;
    ctx.font = 'bold 26px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(title, LOGICAL_WIDTH / 2, 40);

    // Subtitle by outcome.
    ctx.fillStyle = '#c9b896';
    ctx.font = '14px system-ui, sans-serif';
    let subtitle = '';
    if (this.outcome.result === 'victory') {
      subtitle = this.outcome.killQuality === 'perfect' ? 'A clean kill.' : 'The deed is done.';
    } else if (this.outcome.result === 'hero_death') {
      subtitle = 'Word travels. The forge\'s name dims.';
    } else {
      subtitle = 'The blade snapped. The hero retreats.';
    }
    ctx.fillText(subtitle, LOGICAL_WIDTH / 2, 78);

    // Weapon display.
    drawBlade(ctx, this.outcome.weapon.shape, this.outcome.weapon.heat, {
      cx: LOGICAL_WIDTH / 2,
      cy: 200,
      length: 280,
      rotation: -0.18,
      damage: this.outcome.weapon.damage,
    });

    // Stats block.
    ctx.fillStyle = '#c9b896';
    ctx.font = '14px system-ui, sans-serif';
    ctx.textAlign = 'left';
    const px = 60;
    let py = 320;
    if (this.outcome.result === 'victory') {
      ctx.fillText(`Gold earned:   +${this.outcome.goldEarned}`, px, py);
      py += 22;
    }
    const repPrefix = this.outcome.reputationDelta >= 0 ? '+' : '';
    ctx.fillStyle = this.outcome.reputationDelta >= 0 ? '#7fffaa' : '#ff7060';
    ctx.fillText(`Reputation:   ${repPrefix}${this.outcome.reputationDelta}`, px, py);
    py += 22;
    ctx.fillStyle = '#c9b896';
    ctx.fillText(
      `Weapon:       ${this.outcome.weaponSurvived ? 'Survives' : 'Destroyed'}`,
      px,
      py,
    );
    py += 22;
    ctx.fillText(
      `Hero battles: ${this.outcome.weapon.battlesWon}`,
      px,
      py,
    );
    py += 22;
    if (this.outcome.weaponHallOfFameEligible) {
      ctx.fillStyle = '#ffd84a';
      ctx.fillText('Weapon enters Hall of Fame', px, py);
    }

    drawButton(ctx, {
      rect: ADVANCE_BUTTON,
      label: this.advanceLabel(),
      enabled: this.elapsed > 0.8,
      highlight: true,
    });
  }

  private advanceLabel(): string {
    if (this.outcome.result === 'victory') return 'TO THE SHOP';
    return 'CONTINUE';
  }
}
